import { useEffect, useRef, useState } from "react";

const DEBOUNCE_MS = 250;

export default function PromptAutocompleteInput({
  id,
  value = "",
  onChange,
  disabled = false,
  readonly = false,
  options = {},
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const canEdit = !disabled && !readonly;
  const projectId = options.projectId;
  const apiUrl = options.apiUrl;
  const authToken = options.authToken;

  useEffect(() => {
    if (!isOpen || !projectId || !apiUrl) return undefined;

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ project_id: projectId });
        const query = String(value || "").trim();
        if (query) params.set("q", query);
        const response = await fetch(
          `${apiUrl}/tasks/prompt-suggestions?${params.toString()}`,
          {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error("Suggestion request failed");
        const payload = await response.json();
        setSuggestions(Array.isArray(payload?.suggestions) ? payload.suggestions : []);
        setActiveIndex(-1);
      } catch (error) {
        if (error.name !== "AbortError") {
          setSuggestions([]);
          setActiveIndex(-1);
        }
      }
    }, String(value || "").trim() ? DEBOUNCE_MS : 0);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [apiUrl, authToken, isOpen, projectId, value]);

  const selectSuggestion = (suggestion) => {
    onChange?.(suggestion);
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (!isOpen || !suggestions.length) {
      if (event.key === "Escape") setIsOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className="position-relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="form-control"
        value={value || ""}
        disabled={!canEdit}
        readOnly={readonly}
        autoComplete="off"
        onFocus={() => canEdit && setIsOpen(true)}
        onBlur={() => window.setTimeout(() => setIsOpen(false), 150)}
        onChange={(event) => onChange?.(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      {isOpen && suggestions.length > 0 && (
        <ul
          className="list-group position-absolute w-100 shadow-sm"
          role="listbox"
          style={{ zIndex: 30, maxHeight: 220, overflowY: "auto" }}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              className={`list-group-item list-group-item-action${
                index === activeIndex ? " active" : ""
              }`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectSuggestion(suggestion)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
